import { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { ChevronLeft, ArrowRight, Globe, X, Plus, Save } from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { useResponsive } from '@/lib/useResponsive';
import { useProfileStore } from '@/lib/state/profile-store';
import type { StartupProfile } from '@/lib/state/profile-store';
import { validateRequired, validateNumber } from '@/lib/validation';
import { SkipButton } from '@/components/SkipButton';
import { OnboardingLayout, STARTUP_ONBOARDING_STEPS } from '@/components/OnboardingLayout';
import AnimatedPressable from '@/components/AnimatedPressable';

export default function QuestionnaireMarketScreen() {
  const router = useRouter();
  const colors = useSystemThemeColors();
  const { isDesktop } = useResponsive();
  const params = useLocalSearchParams<{
    name: string;
    tagline: string;
    industry: string;
    stage: string;
    teamSize: string;
    location: string;
    foundedYear: string;
    website: string;
    problem: string;
    solution: string;
    targetCustomer: string;
    monthlyRevenue: string;
    userCount: string;
    growthRate: string;
    mode: string;
  }>();

  const isStandalone = params.mode === 'standalone';
  const existingProfile = useProfileStore((s) => s.startupProfile);
  const setProfileStoreProfile = useProfileStore((s) => s.setStartupProfile);

  const setStartupProfile = useProfileStore((s) => s.setStartupProfile);
  const setProfileMethod = useProfileStore((s) => s.setProfileMethod);

  const [marketSize, setMarketSize] = useState(
    isStandalone && existingProfile?.marketSize ? existingProfile.marketSize.toLocaleString() : ''
  );
  const [fundingTarget, setFundingTarget] = useState(
    isStandalone && existingProfile?.fundingTarget ? existingProfile.fundingTarget.toLocaleString() : ''
  );
  const [competitors, setCompetitors] = useState<string[]>(
    isStandalone && existingProfile?.competitors ? existingProfile.competitors : []
  );
  const [newCompetitor, setNewCompetitor] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});

  const formatCurrency = (value: string): string => {
    const num = value.replace(/[^0-9]/g, '');
    if (!num) return '';
    return Number(num).toLocaleString();
  };

  const addCompetitor = () => {
    if (newCompetitor.trim() && competitors.length < 5) {
      setCompetitors([...competitors, newCompetitor.trim()]);
      setNewCompetitor('');
    }
  };

  const removeCompetitor = (index: number) => {
    setCompetitors(competitors.filter((_, i) => i !== index));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!validateRequired(fundingTarget)) {
      newErrors.fundingTarget = 'Funding target is required';
    } else if (!validateNumber(fundingTarget.replace(/[^0-9]/g, ''))) {
      newErrors.fundingTarget = 'Please enter a valid amount';
    }

    if (marketSize && !validateNumber(marketSize.replace(/[^0-9]/g, ''))) {
      newErrors.marketSize = 'Please enter a valid market size';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleComplete = () => {
    if (!validate()) return;

    if (isStandalone && existingProfile) {
      // Standalone mode: save directly to store and go back
      setProfileStoreProfile({
        ...existingProfile,
        fundingTarget: Number(fundingTarget.replace(/[^0-9]/g, '')) || existingProfile.fundingTarget,
        marketSize: marketSize ? Number(marketSize.replace(/[^0-9]/g, '')) : existingProfile.marketSize,
        competitors: competitors.length > 0 ? competitors : existingProfile.competitors,
      });
      router.back();
      return;
    }

    // Build the complete profile from all params
    const profile: StartupProfile = {
      id: `startup-${Date.now()}`,
      name: params.name || '',
      tagline: params.tagline || '',
      industry: params.industry || '',
      stage: (params.stage as 'pre-seed' | 'seed' | 'series-a') || 'pre-seed',
      teamSize: Number(params.teamSize) || 1,
      location: params.location || '',
      foundedYear: Number(params.foundedYear) || new Date().getFullYear(),
      website: params.website,
      fundingTarget: Number(fundingTarget.replace(/[^0-9]/g, '')) || 0,
      profileSource: 'questionnaire',
      problem: params.problem,
      solution: params.solution,
      targetCustomer: params.targetCustomer,
      monthlyRevenue: Number(params.monthlyRevenue) || 0,
      userCount: Number(params.userCount) || 0,
      growthRate: Number(params.growthRate) || 0,
      marketSize: marketSize ? Number(marketSize.replace(/[^0-9]/g, '')) : undefined,
      competitors: competitors.length > 0 ? competitors : undefined,
      isActivelyRaising: false,
      dataRoomConnected: false,
      isSerialFounder: false,
    };

    // Save to store
    setStartupProfile(profile);
    setProfileMethod?.('questionnaire');

    // Navigate to complete screen
    router.push('/(onboarding)/complete');
  };

  // Form content (shared between mobile and desktop)
  const formContent = (
    <View className="gap-5">
      {/* Market Size */}
      <View>
        <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
          Total Addressable Market (TAM)
        </Text>
        <View
          className="flex-row items-center rounded-xl px-4"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: errors.marketSize ? colors.red.main : colors.border.light,
          }}
        >
          <Text style={{ color: colors.text.tertiary }} className="text-lg mr-2">
            $
          </Text>
          <TextInput
            style={{
              flex: 1,
              paddingVertical: 16,
              color: colors.text.primary,
              fontSize: 16,
            }}
            placeholder="e.g., 5,000,000,000"
            placeholderTextColor={colors.text.tertiary}
            value={marketSize}
            onChangeText={(text) => {
              setMarketSize(formatCurrency(text));
              if (errors.marketSize) setErrors((e) => ({ ...e, marketSize: '' }));
            }}
            keyboardType="number-pad"
          />
        </View>
        {errors.marketSize ? (
          <Text style={{ color: colors.red.main }} className="text-xs mt-1">
            {errors.marketSize}
          </Text>
        ) : (
          <Text style={{ color: colors.text.tertiary }} className="text-xs mt-1">
            The total market opportunity for your product
          </Text>
        )}
      </View>

      {/* Funding Target */}
      <View>
        <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
          Funding Target <Text style={{ color: colors.red.main }}>*</Text>
        </Text>
        <View
          className="flex-row items-center rounded-xl px-4"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: errors.fundingTarget ? colors.red.main : colors.border.light,
          }}
        >
          <Text style={{ color: colors.text.tertiary }} className="text-lg mr-2">
            $
          </Text>
          <TextInput
            style={{
              flex: 1,
              paddingVertical: 16,
              color: colors.text.primary,
              fontSize: 16,
            }}
            placeholder="e.g., 500,000"
            placeholderTextColor={colors.text.tertiary}
            value={fundingTarget}
            onChangeText={(text) => {
              setFundingTarget(formatCurrency(text));
              if (errors.fundingTarget) setErrors((e) => ({ ...e, fundingTarget: '' }));
            }}
            keyboardType="number-pad"
          />
        </View>
        {errors.fundingTarget && (
          <Text style={{ color: colors.red.main }} className="text-xs mt-1">
            {errors.fundingTarget}
          </Text>
        )}
      </View>

      {/* Competitors */}
      <View>
        <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
          Key Competitors
        </Text>
        <Text style={{ color: colors.text.tertiary }} className="text-xs mb-3">
          Add up to 5 competitors
        </Text>

        {/* Competitor Chips */}
        {competitors.length > 0 && (
          <View className="flex-row flex-wrap gap-2 mb-3">
            {competitors.map((comp, index) => (
              <View
                key={index}
                className="flex-row items-center gap-2 px-3 py-2 rounded-full"
                style={{ backgroundColor: colors.primary[50] }}
              >
                <Text style={{ color: colors.primary[700] }} className="text-sm">
                  {comp}
                </Text>
                <AnimatedPressable onPress={() => removeCompetitor(index)}>
                  <X size={16} color={colors.primary[600]} />
                </AnimatedPressable>
              </View>
            ))}
          </View>
        )}

        {/* Add Competitor Input */}
        {competitors.length < 5 && (
          <View className="flex-row gap-2">
            <TextInput
              style={{
                flex: 1,
                backgroundColor: colors.surface.primary,
                borderWidth: 1,
                borderColor: colors.border.light,
                borderRadius: 12,
                padding: 16,
                color: colors.text.primary,
                fontSize: 16,
              }}
              placeholder="e.g., Competitor name"
              placeholderTextColor={colors.text.tertiary}
              value={newCompetitor}
              onChangeText={setNewCompetitor}
              onSubmitEditing={addCompetitor}
              returnKeyType="done"
            />
            <AnimatedPressable
              onPress={addCompetitor}
              className="w-14 h-14 rounded-xl items-center justify-center"
              style={{ backgroundColor: colors.primary[600] }}
            >
              <Plus size={24} color={colors.text.inverse} />
            </AnimatedPressable>
          </View>
        )}
      </View>

      {/* Complete / Save Button */}
      <View className="mt-4">
        <AnimatedPressable
          onPress={handleComplete}
          className="rounded-xl py-4 flex-row items-center justify-center gap-2"
          style={{ backgroundColor: colors.primary[600] }}
        >
          {isStandalone ? (
            <>
              <Save size={20} color={colors.text.inverse} />
              <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
                Save
              </Text>
            </>
          ) : (
            <>
              <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
                Complete Profile
              </Text>
              <ArrowRight size={20} color={colors.text.inverse} />
            </>
          )}
        </AnimatedPressable>
      </View>
    </View>
  );

  // Desktop layout with OnboardingLayout
  if (isWeb && isDesktop) {
    return (
      <OnboardingLayout
        // Enhance-Profile screen, not part of the canonical onboarding flow —
        // no currentStep id to highlight (see src/lib/onboarding/steps.ts).
        steps={STARTUP_ONBOARDING_STEPS}
        title="Market & Fundraising"
        subtitle="Almost done! Tell us about your market"
        userType="startup"
        entityName={params.name}
      >
        {formContent}
      </OnboardingLayout>
    );
  }

  // Mobile layout
  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={['top', 'bottom']} className="flex-1">
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          className="flex-1"
        >
          {/* Header */}
          <Animated.View
            entering={FadeInDown.delay(100).duration(500)}
            className="px-5 pt-4 pb-4"
          >
            <View className="flex-row items-center justify-between">
              <Pressable
                onPress={() => router.back()}
                className="w-10 h-10 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <ChevronLeft size={24} color={colors.text.primary} />
              </Pressable>
              <View className="flex-row gap-2">
                <View
                  className="h-1 w-8 rounded-full"
                  style={{ backgroundColor: colors.primary[500] }}
                />
                <View
                  className="h-1 w-8 rounded-full"
                  style={{ backgroundColor: colors.primary[500] }}
                />
                <View
                  className="h-1 w-8 rounded-full"
                  style={{ backgroundColor: colors.primary[500] }}
                />
                <View
                  className="h-1 w-8 rounded-full"
                  style={{ backgroundColor: colors.primary[500] }}
                />
              </View>
              <View className="w-10" />
            </View>
          </Animated.View>

          {/* Title */}
          <Animated.View
            entering={FadeInDown.delay(200).duration(500)}
            className="px-5 pb-6"
          >
            <View className="flex-row items-center gap-3 mb-2">
              <Globe size={24} color={colors.primary[600]} />
              <Text style={{ color: colors.text.tertiary }} className="text-sm font-medium">
                Step 4 of 4
              </Text>
            </View>
            <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">
              Market & Fundraising
            </Text>
            <Text style={{ color: colors.text.secondary }} className="text-base mt-1">
              Almost done! Tell us about your market
            </Text>
          </Animated.View>

          {/* Form */}
          <ScrollView
            className="flex-1 px-5"
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            <Animated.View entering={FadeInUp.delay(300).duration(500)} className="pb-8">
              {formContent}
            </Animated.View>
          </ScrollView>

          {/* Skip Button */}
          <SkipButton
            step="profile-details"
            nextRoute="/(onboarding)/complete"
            label="Skip for now"
            delay={500}
          />
        </KeyboardAvoidingView>
      </SafeAreaView>
    </View>
  );
}
